<template>
  <div class="dev-analytics">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-chart-timeline-variant" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">📊 Analytics del Desarrollo</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Métricas y estadísticas del sistema</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 📊 Key Metrics Dashboard -->
    <v-row class="mb-6">
      <v-col
        v-for="metric in keyMetrics"
        :key="metric.label"
        cols="12"
        md="3"
      >
        <v-card elevation="2" class="text-center pa-4 metric-card">
          <v-avatar :color="metric.color" size="56" class="mb-3">
            <v-icon :icon="metric.icon" color="white" size="28" />
          </v-avatar>
          <h2 class="text-h4 mb-1">{{ metric.value }}</h2>
          <p class="text-subtitle-2 mb-2">{{ metric.label }}</p>
          <v-chip
            :color="metric.trend === 'up' ? 'success' : metric.trend === 'down' ? 'error' : 'warning'"
            size="small"
            variant="flat"
          >
            <v-icon
              :icon="metric.trend === 'up' ? 'mdi-trending-up' : metric.trend === 'down' ? 'mdi-trending-down' : 'mdi-trending-neutral'"
              size="14"
              class="me-1"
            />
            {{ metric.change }}
          </v-chip>
        </v-card>
      </v-col>
    </v-row>

    <!-- 📈 Charts Section -->
    <v-row class="mb-6">
      <!-- Performance Chart -->
      <v-col cols="12" md="8">
        <v-card elevation="2">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-speedometer" class="me-2" />
            Rendimiento del Sistema
            <v-spacer />
            <v-select
              v-model="performanceTimeRange"
              :items="timeRanges"
              variant="outlined"
              density="compact"
              hide-details
              style="max-width: 120px;"
            />
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="chart-container">
              <canvas
                ref="performanceChart"
                class="performance-chart"
                style="max-height: 300px;"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- System Health -->
      <v-col cols="12" md="4">
        <v-card elevation="2" class="h-100">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-heart-pulse" class="me-2" />
            Salud del Sistema
          </v-card-title>
          <v-card-text class="pa-4">
            <div
              v-for="health in systemHealth"
              :key="health.component"
              class="mb-4"
            >
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-2">{{ health.component }}</span>
                <v-chip
                  :color="health.status === 'healthy' ? 'success' : health.status === 'warning' ? 'warning' : 'error'"
                  size="small"
                  variant="flat"
                >
                  {{ health.status }}
                </v-chip>
              </div>
              <v-progress-linear
                :model-value="health.value"
                :color="health.status === 'healthy' ? 'success' : health.status === 'warning' ? 'warning' : 'error'"
                height="8"
                rounded
              />
              <div class="text-caption text-medium-emphasis mt-1">
                {{ health.value }}% - {{ health.description }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🔍 API Analytics -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-api" class="me-2" />
            Top APIs por Uso
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list>
              <v-list-item
                v-for="api in topApis"
                :key="api.endpoint"
                :prepend-icon="api.icon"
              >
                <v-list-item-title>{{ api.endpoint }}</v-list-item-title>
                <v-list-item-subtitle>{{ api.requests }} requests</v-list-item-subtitle>
                <template #append>
                  <div class="text-end">
                    <div class="text-caption">{{ api.avgResponse }}ms</div>
                    <v-progress-linear
                      :model-value="(api.requests / maxRequests) * 100"
                      color="primary"
                      height="4"
                      style="width: 80px;"
                    />
                  </div>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Error Analysis -->
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-alert-circle" class="me-2" />
            Análisis de Errores
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list>
              <v-list-item
                v-for="error in errorAnalysis"
                :key="error.type"
                :prepend-icon="error.icon"
              >
                <v-list-item-title>{{ error.type }}</v-list-item-title>
                <v-list-item-subtitle>{{ error.description }}</v-list-item-subtitle>
                <template #append>
                  <div class="text-end">
                    <v-chip
                      :color="error.severity === 'high' ? 'error' : error.severity === 'medium' ? 'warning' : 'info'"
                      size="small"
                      variant="flat"
                    >
                      {{ error.count }}
                    </v-chip>
                  </div>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 👥 User Activity -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-account-group" class="me-2" />
        Actividad de Usuarios
        <v-spacer />
        <v-btn
          variant="outlined"
          size="small"
          prepend-icon="mdi-refresh"
          @click="refreshUserActivity"
        >
          Actualizar
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4">
        <v-row>
          <v-col
            v-for="activity in userActivity"
            :key="activity.role"
            cols="12"
            md="4"
          >
            <div class="activity-card pa-3">
              <div class="d-flex align-center mb-3">
                <v-avatar :color="activity.color" size="32" class="me-2">
                  <v-icon :icon="activity.icon" color="white" size="16" />
                </v-avatar>
                <div>
                  <h4 class="text-subtitle-1 mb-0">{{ activity.role }}</h4>
                  <p class="text-caption mb-0 text-medium-emphasis">{{ activity.activeUsers }} usuarios activos</p>
                </div>
              </div>
              <div class="mb-2">
                <div class="d-flex justify-space-between text-caption">
                  <span>Actividad</span>
                  <span>{{ activity.activityLevel }}%</span>
                </div>
                <v-progress-linear
                  :model-value="activity.activityLevel"
                  :color="activity.color"
                  height="6"
                  rounded
                />
              </div>
              <div class="text-caption text-medium-emphasis">
                Última actividad: {{ activity.lastActivity }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 🔄 Back to Dev Center -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center')"
        >
          Volver al Dev Center
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-download"
          @click="exportAnalytics"
        >
          Exportar Datos
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface Metric {
  label: string
  value: string
  icon: string
  color: string
  trend: 'up' | 'down' | 'stable'
  change: string
}

interface SystemHealth {
  component: string
  value: number
  status: 'healthy' | 'warning' | 'error'
  description: string
}

interface ApiUsage {
  endpoint: string
  requests: number
  avgResponse: number
  icon: string
}

interface ErrorData {
  type: string
  description: string
  count: number
  severity: 'high' | 'medium' | 'low'
  icon: string
}

interface UserActivity {
  role: string
  activeUsers: number
  activityLevel: number
  lastActivity: string
  color: string
  icon: string
}

// 🔧 Reactive state
const store = useMainStore()
const performanceChart = ref<HTMLCanvasElement>()
const performanceTimeRange = ref('7d')

// 📊 Mock data
const keyMetrics = ref<Metric[]>([
  {
    label: 'Uptime',
    value: '99.9%',
    icon: 'mdi-server',
    color: 'success',
    trend: 'up',
    change: '+0.2%'
  },
  {
    label: 'Response Time',
    value: '145ms',
    icon: 'mdi-speedometer',
    color: 'info',
    trend: 'down',
    change: '-12ms'
  },
  {
    label: 'Active Users',
    value: '2,847',
    icon: 'mdi-account-group',
    color: 'primary',
    trend: 'up',
    change: '+156'
  },
  {
    label: 'Error Rate',
    value: '0.8%',
    icon: 'mdi-alert-circle',
    color: 'warning',
    trend: 'stable',
    change: '±0%'
  }
])

const timeRanges = ref([
  { title: '7 días', value: '7d' },
  { title: '30 días', value: '30d' },
  { title: '90 días', value: '90d' }
])

const systemHealth = ref<SystemHealth[]>([
  {
    component: 'Base de Datos',
    value: 95,
    status: 'healthy',
    description: 'Óptimo rendimiento'
  },
  {
    component: 'API Gateway',
    value: 88,
    status: 'warning',
    description: 'Carga moderada'
  },
  {
    component: 'Cache Redis',
    value: 97,
    status: 'healthy',
    description: 'Funcionando bien'
  },
  {
    component: 'File Storage',
    value: 72,
    status: 'warning',
    description: 'Espacio limitado'
  }
])

const topApis = ref<ApiUsage[]>([
  { endpoint: '/api/auth/login', requests: 15420, avgResponse: 89, icon: 'mdi-login' },
  { endpoint: '/api/incidents/list', requests: 12350, avgResponse: 145, icon: 'mdi-format-list-bulleted' },
  { endpoint: '/api/users/profile', requests: 9870, avgResponse: 67, icon: 'mdi-account' },
  { endpoint: '/api/reports/generate', requests: 7650, avgResponse: 234, icon: 'mdi-file-chart' },
  { endpoint: '/api/notifications/send', requests: 6540, avgResponse: 45, icon: 'mdi-bell' }
])

const maxRequests = Math.max(...topApis.value.map(api => api.requests))

const errorAnalysis = ref<ErrorData[]>([
  {
    type: '404 Not Found',
    description: 'Recursos no encontrados',
    count: 23,
    severity: 'low',
    icon: 'mdi-file-question'
  },
  {
    type: '500 Server Error',
    description: 'Errores internos del servidor',
    count: 7,
    severity: 'high',
    icon: 'mdi-server-off'
  },
  {
    type: '401 Unauthorized',
    description: 'Accesos no autorizados',
    count: 45,
    severity: 'medium',
    icon: 'mdi-lock-alert'
  },
  {
    type: 'Timeout Errors',
    description: 'Timeouts de conexión',
    count: 12,
    severity: 'medium',
    icon: 'mdi-clock-alert'
  }
])

const userActivity = ref<UserActivity[]>([
  {
    role: 'Operadores',
    activeUsers: 45,
    activityLevel: 87,
    lastActivity: 'hace 2 min',
    color: 'orange',
    icon: 'mdi-truck'
  },
  {
    role: 'Supervisores',
    activeUsers: 12,
    activityLevel: 72,
    lastActivity: 'hace 5 min',
    color: 'blue',
    icon: 'mdi-account-supervisor'
  },
  {
    role: 'Administradores',
    activeUsers: 3,
    activityLevel: 95,
    lastActivity: 'hace 1 min',
    color: 'red',
    icon: 'mdi-shield-crown'
  }
])

// 🎯 Methods
const refreshUserActivity = () => {
  console.log('🔄 Actualizando actividad de usuarios')
  store.showNotification({
    type: 'success',
    title: 'Actualizado',
    message: 'Datos de actividad actualizados'
  })
}

const exportAnalytics = () => {
  console.log('📊 Exportando analytics')
  store.showNotification({
    type: 'success',
    title: 'Exportación Iniciada',
    message: 'Generando reporte de analytics...'
  })
}

const initializeChart = () => {
  nextTick(() => {
    if (performanceChart.value) {
      const ctx = performanceChart.value.getContext('2d')
      if (ctx) {
        // Simular datos del gráfico
        console.log('📈 Inicializando gráfico de rendimiento')
        // Aquí iría la implementación real con Chart.js o similar
      }
    }
  })
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('📊 Vista de analytics de desarrollo cargada')
  initializeChart()
})
</script>

<style scoped>
.dev-analytics {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.metric-card {
  transition: transform 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

.performance-chart {
  max-height: 300px;
  width: 100%;
}

.activity-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.activity-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #3f51b5 0%, #9c27b0 100%);
}
</style>
